/**
 * ConnectionModel - Pure data layer for cable connections between device ports
 * 
 * Wraps state.connections with CRUD operations. No DOM, no side effects beyond data mutation.
 * 
 * Connection structure:
 *   {
 *     id: string,
 *     sourceDeviceId: string,
 *     sourceGroupIndex: number,
 *     sourcePortType: string,       // 'audio', 'midi', etc.
 *     sourcePortIndex: number,
 *     targetDeviceId: string,
 *     targetGroupIndex: number,
 *     targetPortType: string,
 *     targetPortIndex: number,
 *     sourceDirection?: string,     // 'insert' for bidirectional ports
 *     targetDirection?: string      // 'insert' for bidirectional ports
 *   }
 * 
 * Usage:
 *   import { createConnectionModel } from './models/ConnectionModel.js';
 *   const connectionModel = createConnectionModel(state);
 *   // Now use connectionModel.getAll(), connectionModel.add(), etc.
 */

import { generateId } from '../utils/index.js';

/**
 * Create a ConnectionModel that wraps the connections array in the given state object.
 * 
 * @param {Object} state - The state object containing state.connections array
 * @param {Object} options - Optional helpers for group resolution
 * @param {function} options.getSourceGroup - Function to resolve source group index (for legacy format)
 * @param {function} options.getTargetGroup - Function to resolve target group index (for legacy format)
 * @returns {ConnectionModel}
 */
export function createConnectionModel(state, options = {}) {
    const { getSourceGroup, getTargetGroup } = options;
    
    // Helper to resolve source group index (handles legacy format)
    const resolveSourceGroup = (conn) => {
        if (getSourceGroup) return getSourceGroup(conn);
        return conn.sourceGroupIndex;
    };
    
    // Helper to resolve target group index (handles legacy format)
    const resolveTargetGroup = (conn) => {
        if (getTargetGroup) return getTargetGroup(conn);
        return conn.targetGroupIndex;
    };
    
    return {
        // ============= READ OPERATIONS =============
        
        /**
         * Get all connections
         * @returns {Connection[]}
         */
        getAll() {
            return state.connections;
        },
        
        /**
         * Get connection by ID
         * @param {string} id
         * @returns {Connection|undefined}
         */
        getById(id) {
            return state.connections.find(c => c.id === id);
        },
        
        /**
         * Get connection count
         * @returns {number}
         */
        getCount() {
            return state.connections.length;
        },
        
        /**
         * Check if a connection exists
         * @param {string} id
         * @returns {boolean}
         */
        exists(id) {
            return state.connections.some(c => c.id === id);
        },
        
        /**
         * Check if any connection matches predicate
         * @param {function(Connection): boolean} predicate
         * @returns {boolean}
         */
        some(predicate) {
            return state.connections.some(predicate);
        },
        
        /**
         * Filter connections by predicate
         * @param {function(Connection): boolean} predicate
         * @returns {Connection[]}
         */
        filter(predicate) {
            return state.connections.filter(predicate);
        },
        
        /**
         * Iterate over all connections
         * @param {function(Connection): void} callback
         */
        forEach(callback) {
            state.connections.forEach(callback);
        },
        
        /**
         * Map over all connections
         * @param {function(Connection): T} mapper
         * @returns {T[]}
         */
        map(mapper) {
            return state.connections.map(mapper);
        },
        
        // ============= DEVICE QUERIES =============
        
        /**
         * Get all connections involving a device (as source or target)
         * @param {string} deviceId
         * @returns {Connection[]}
         */
        getByDeviceId(deviceId) {
            return state.connections.filter(c => 
                c.sourceDeviceId === deviceId || c.targetDeviceId === deviceId
            );
        },
        
        /**
         * Get all connections where device is the source
         * @param {string} deviceId
         * @returns {Connection[]}
         */
        getBySourceDeviceId(deviceId) {
            return state.connections.filter(c => c.sourceDeviceId === deviceId);
        },
        
        /**
         * Get all connections where device is the target
         * @param {string} deviceId
         * @returns {Connection[]}
         */
        getByTargetDeviceId(deviceId) {
            return state.connections.filter(c => c.targetDeviceId === deviceId);
        },
        
        /**
         * Check if a device has any connections
         * @param {string} deviceId
         * @returns {boolean}
         */
        hasConnectionsForDevice(deviceId) {
            return state.connections.some(c => 
                c.sourceDeviceId === deviceId || c.targetDeviceId === deviceId
            );
        },
        
        /**
         * Get connections between two specific devices
         * @param {string} deviceId1
         * @param {string} deviceId2
         * @returns {Connection[]}
         */
        getBetweenDevices(deviceId1, deviceId2) {
            return state.connections.filter(c =>
                (c.sourceDeviceId === deviceId1 && c.targetDeviceId === deviceId2) ||
                (c.sourceDeviceId === deviceId2 && c.targetDeviceId === deviceId1)
            );
        },
        
        // ============= PORT QUERIES =============
        
        /**
         * Get connections for a specific source port
         * @param {string} deviceId
         * @param {number} groupIndex
         * @param {number} portIndex
         * @returns {Connection[]}
         */
        getBySourcePort(deviceId, groupIndex, portIndex) {
            return state.connections.filter(c => {
                const srcGroup = resolveSourceGroup(c);
                return c.sourceDeviceId === deviceId &&
                    srcGroup === groupIndex &&
                    c.sourcePortIndex === portIndex;
            });
        },
        
        /**
         * Get connections for a specific target port
         * @param {string} deviceId
         * @param {number} groupIndex
         * @param {number} portIndex
         * @returns {Connection[]}
         */
        getByTargetPort(deviceId, groupIndex, portIndex) {
            return state.connections.filter(c => {
                const tgtGroup = resolveTargetGroup(c);
                return c.targetDeviceId === deviceId &&
                    tgtGroup === groupIndex &&
                    c.targetPortIndex === portIndex;
            });
        },
        
        /**
         * Get all connections for a port (as source or target)
         * @param {string} deviceId
         * @param {number} groupIndex
         * @param {string} portType - 'output', 'input', or 'insert'
         * @param {number} portIndex
         * @returns {Connection[]}
         */
        getByPort(deviceId, groupIndex, portType, portIndex) {
            return state.connections.filter(c => {
                const srcGroup = resolveSourceGroup(c);
                const tgtGroup = resolveTargetGroup(c);
                
                // Check source side
                const isSource = c.sourceDeviceId === deviceId &&
                    srcGroup === groupIndex &&
                    c.sourcePortIndex === portIndex;
                
                // Check target side
                const isTarget = c.targetDeviceId === deviceId &&
                    tgtGroup === groupIndex &&
                    c.targetPortIndex === portIndex;
                
                return isSource || isTarget;
            });
        },
        
        /**
         * Check if a connection exists between specific ports
         * @param {Object} source - { deviceId, groupIndex, portIndex }
         * @param {Object} target - { deviceId, groupIndex, portIndex }
         * @returns {boolean}
         */
        existsBetweenPorts(source, target) {
            return state.connections.some(c => {
                const srcGroup = resolveSourceGroup(c);
                const tgtGroup = resolveTargetGroup(c);
                return c.sourceDeviceId === source.deviceId &&
                    srcGroup === source.groupIndex &&
                    c.sourcePortIndex === source.portIndex &&
                    c.targetDeviceId === target.deviceId &&
                    tgtGroup === target.groupIndex &&
                    c.targetPortIndex === target.portIndex;
            });
        },
        
        /**
         * Check if any port in a group has connections
         * @param {string} deviceId
         * @param {number} groupIndex
         * @param {string} direction - 'output' or 'input'
         * @returns {boolean}
         */
        groupHasConnections(deviceId, groupIndex, direction) {
            return state.connections.some(c => {
                if (direction === 'output') {
                    const srcGroup = resolveSourceGroup(c);
                    return c.sourceDeviceId === deviceId && srcGroup === groupIndex;
                } else {
                    const tgtGroup = resolveTargetGroup(c);
                    return c.targetDeviceId === deviceId && tgtGroup === groupIndex;
                }
            });
        },
        
        // ============= WRITE OPERATIONS =============
        
        /**
         * Add a connection
         * @param {Connection} connection
         * @returns {Connection} The added connection
         */
        add(connection) {
            state.connections.push(connection);
            return connection;
        },
        
        /**
         * Add multiple connections
         * @param {Connection[]} connections
         */
        addMany(connections) {
            connections.forEach(c => state.connections.push(c));
        },
        
        /**
         * Remove a connection by ID
         * @param {string} id
         * @returns {Connection|undefined} The removed connection, if found
         */
        remove(id) {
            const index = state.connections.findIndex(c => c.id === id);
            if (index === -1) return undefined;
            const [removed] = state.connections.splice(index, 1);
            return removed;
        },
        
        /**
         * Remove multiple connections by IDs
         * @param {string[]} ids
         * @returns {Connection[]} The removed connections
         */
        removeMany(ids) {
            const idSet = new Set(ids);
            const removed = state.connections.filter(c => idSet.has(c.id));
            state.connections = state.connections.filter(c => !idSet.has(c.id));
            return removed;
        },
        
        /**
         * Remove all connections involving a device
         * @param {string} deviceId
         * @returns {Connection[]} The removed connections
         */
        removeByDeviceId(deviceId) {
            const removed = state.connections.filter(c => 
                c.sourceDeviceId === deviceId || c.targetDeviceId === deviceId
            );
            state.connections = state.connections.filter(c => 
                c.sourceDeviceId !== deviceId && c.targetDeviceId !== deviceId
            );
            return removed;
        },
        
        /**
         * Remove connections matching a predicate
         * @param {function(Connection): boolean} predicate
         * @returns {Connection[]} The removed connections
         */
        removeWhere(predicate) {
            const removed = state.connections.filter(predicate);
            state.connections = state.connections.filter(c => !predicate(c));
            return removed;
        },
        
        /**
         * Clear all connections
         * @returns {Connection[]} The removed connections
         */
        clear() {
            const removed = [...state.connections];
            state.connections = [];
            return removed;
        },
        
        /**
         * Replace all connections (for loading patches)
         * @param {Connection[]} connections
         */
        replaceAll(connections) {
            state.connections = connections;
        },
        
        // ============= FACTORY =============
        
        /**
         * Create and add a new connection
         * @param {Object} params - Connection parameters
         * @returns {Connection}
         */
        create({
            sourceDeviceId,
            sourceGroupIndex,
            sourcePortType,
            sourcePortIndex,
            targetDeviceId,
            targetGroupIndex,
            targetPortType,
            targetPortIndex,
            sourceDirection,
            targetDirection
        }) {
            const connection = {
                id: generateId(),
                sourceDeviceId,
                sourceGroupIndex,
                sourcePortType,
                sourcePortIndex,
                targetDeviceId,
                targetGroupIndex,
                targetPortType,
                targetPortIndex,
                // Only store directions if not default (for insert ports)
                ...(sourceDirection === 'insert' && { sourceDirection: 'insert' }),
                ...(targetDirection === 'insert' && { targetDirection: 'insert' })
            };
            state.connections.push(connection);
            return connection;
        },
        
        /**
         * Clone a connection with new ID and optional device remapping
         * @param {Connection} connection
         * @param {Object} [deviceIdMap] - Map of old device IDs to new IDs
         * @returns {Connection}
         */
        clone(connection, deviceIdMap = null) {
            const cloned = {
                ...JSON.parse(JSON.stringify(connection)),
                id: generateId(),
                ...(deviceIdMap && {
                    sourceDeviceId: deviceIdMap[connection.sourceDeviceId] || connection.sourceDeviceId,
                    targetDeviceId: deviceIdMap[connection.targetDeviceId] || connection.targetDeviceId
                })
            };
            state.connections.push(cloned);
            return cloned;
        }
    };
}
